import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import type { QuickInstruction } from '@jerome2/shared';
import { Icon, type IconName } from '../ui/Icon.tsx';
import { useStore } from '../lib/store.ts';
import { app } from '../state/app.ts';
import { useVoiceCapability, VOICE_OFF_TITLE } from '../voice/capability.ts';
import { showVoiceError, VoiceError } from '../voice/errors.ts';

// De donkere instructiebalk onder een open mail; op de telefoon en op de desktop dezelfde component.
// Rij 1 (alleen bij bijsturen of een vraag): de chip met de taak of de vraag. Rij 2: de snelknoppen
// in één rij die zijwaarts schuift zonder schuifbalk. Rij 3, nooit wikkelend: microfoon, tekstvak,
// Volgende en versturen. De wortel is een container (containerquery): in een paneel smaller dan
// 40 rem en op aanraakschermen zijn Volgende en versturen iconknoppen van 44 px, breder staan tekst
// en sneltoets erbij. Enter verstuurt; Shift+Enter maakt een nieuwe regel. Elke instructie is een
// nieuwe taak; bijsturen van een bestaande taak gebeurt alleen via de knop Stuur bij (stand steer,
// met een chip die de taak noemt en een kruisje om terug te gaan). Een snelknop gaat alleen bij een
// nieuwe instructie direct weg; bij bijsturen of een vraag komt de tekst in het veld. Mislukt
// versturen, dan blijft de tekst staan (de ouder toont de fout). Zonder ElevenLabs-sleutel is de
// microfoon gedempt: een klik geeft één melding in plaats van een poging.

export type BarMode = 'instruct' | 'steer' | 'answer' | 'none';
export type BarSource = 'text' | 'voice';

export interface InstructionBarHandle {
  focus(): void;
  /** Zet de tekst in het veld en verstuurt die meteen als `submit` waar is; `source` zegt waar de tekst vandaan komt. */
  insert(text: string, submit?: boolean, source?: BarSource): void;
  /** Gedicteerde tekst (deelresultaat of afgesloten segment) in het veld; telt als spraak. */
  setValue(text: string): void;
  /** Wat er nu in het veld staat (bijvoorbeeld om daarachter verder te dicteren). */
  getValue(): string;
}

export interface InstructionBarProps {
  mode: BarMode;
  quick: QuickInstruction[];
  onSubmit: (text: string, source: BarSource) => Promise<void> | void;
  onNext: () => void;
  hasNext: boolean;
  question?: string | null | undefined;
  /** Bij bijsturen: de instructie van de taak die bijgestuurd wordt. */
  steerOf?: string | null | undefined;
  onCancelSteer?: (() => void) | undefined;
  onMic?: (() => void) | undefined;
  listening?: boolean | undefined;
  partial?: string | undefined;
}

/** Een snelknop waarvan de tekst op een dubbele punt of spatie eindigt wil aangevuld worden; de rest gaat direct weg. */
export const submitsDirectly = (text: string) => !/[:\s]$/.test(text);

const PLACEHOLDER: Record<BarMode, string> = {
  instruct: 'Wat moet er met deze mail gebeuren?',
  steer: 'Stuur bij: wat moet er anders aan dit resultaat?',
  answer: 'Antwoord op de vraag',
  none: 'Kies een mail',
};
/** Op de telefoon is het tekstvak zo'n 23 tekens breed: een langere placeholder wikkelt en maakt de balk een regel hoger. */
const PLACEHOLDER_NARROW: Record<BarMode, string> = {
  instruct: 'Geef een instructie',
  steer: 'Wat moet er anders?',
  answer: 'Je antwoord',
  none: 'Kies een mail',
};
const SUBMIT_LABEL: Record<BarMode, string> = { instruct: 'Naar de wachtrij', steer: 'Stuur bij', answer: 'Beantwoord', none: 'Naar de wachtrij' };

export const InstructionBar = forwardRef<InstructionBarHandle, InstructionBarProps>(function InstructionBar(props, ref) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState<BarSource>('text');
  const area = useRef<HTMLTextAreaElement>(null);
  const off = props.mode === 'none';
  const narrow = useStore(app, (s) => s.narrow);
  const voice = useVoiceCapability();
  const micMuted = !voice.dictation;

  useImperativeHandle(ref, () => ({
    focus: () => area.current?.focus(),
    insert(text, submit, src = 'text') {
      setValue(text);
      setSource(src);
      area.current?.focus();
      if (submit) void doSubmit(text, src);
    },
    setValue(text) {
      setValue(text);
      setSource('voice');
    },
    getValue: () => value,
  }));

  // Het tekstvak groeit mee met de tekst: één regel (44 px) tot hooguit 160 px.
  const fit = useCallback(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.min(160, Math.max(44, el.scrollHeight))}px`;
  }, []);
  useEffect(() => fit(), [value, fit]);
  // Ook opnieuw als het vak breder of smaller wordt (ticket 28625: een andere plek van het leesvenster, de lijst ingeklapt of
  // gesleept, de kolommen die na het laden wijken); anders bleef de hoogte van een smallere stand staan, zoals een lege balk van
  // vijf regels hoog. Alleen een andere breedte telt (de eigen hoogte verandert door fit zelf), en fit wacht een frame, zodat de
  // ResizeObserver niet in een lus komt.
  useEffect(() => {
    const el = area.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let width = el.clientWidth;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === width) return;
      width = el.clientWidth;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [fit]);

  useEffect(() => {
    if (props.partial !== undefined) setValue(props.partial);
  }, [props.partial]);

  // Mislukt versturen, dan heeft de ouder de fout al gemeld; de tekst blijft staan voor een nieuwe poging.
  const doSubmit = async (text: string, src: BarSource = source) => {
    const t = text.trim();
    if (!t || busy || off) return;
    setBusy(true);
    try {
      await props.onSubmit(t, src);
      setValue('');
      setSource('text');
    } catch {
      // gemeld door de ouder
    } finally {
      setBusy(false);
    }
  };

  const pick = (q: QuickInstruction) => {
    setValue(q.text);
    setSource('text');
    area.current?.focus();
    if (props.mode === 'instruct' && submitsDirectly(q.text)) void doSubmit(q.text, 'text');
  };

  const submitLabel = SUBMIT_LABEL[props.mode];

  return (
    <div className="@container shrink-0 bg-bar text-white px-4 pt-2 md:pt-3 pb-safe" data-testid="instructiebalk">
      {props.mode === 'steer' && props.steerOf ? (
        <Chip icon="bolt" text={`Bijsturen: "${props.steerOf}"`}>
          <button type="button" aria-label="Niet bijsturen, nieuwe instructie geven" title="Niet bijsturen (Esc)" onClick={props.onCancelSteer} className="shrink-0 h-6 w-6 rounded-full flex items-center justify-center hover:bg-white/20">
            <Icon name="x" size={12} />
          </button>
        </Chip>
      ) : props.mode === 'answer' && props.question ? (
        <Chip icon="question" text={`Vraag: "${props.question}"`} />
      ) : null}
      <QuickRow quick={props.quick} disabled={off} onPick={pick} />
      <div className="flex flex-nowrap items-end gap-2 pb-2 md:pb-3">
        <button
          type="button"
          aria-label={props.listening ? 'Stop met luisteren' : 'Spreek een instructie in'}
          aria-pressed={props.listening}
          aria-disabled={micMuted || undefined}
          title={micMuted ? VOICE_OFF_TITLE : undefined}
          onClick={micMuted ? () => showVoiceError(new VoiceError('off'), 'dictation') : props.onMic}
          disabled={!props.onMic || off}
          className={`h-11 w-11 rounded-full flex items-center justify-center shrink-0 transition-colors bg-amber ${props.listening ? 'animate-pulse' : micMuted ? 'opacity-40' : 'hover:brightness-110'} disabled:opacity-40`}
        >
          <Icon name="mic" size={20} />
        </button>
        <textarea
          ref={area}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setSource('text');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void doSubmit(value);
            }
            if (e.key === 'Escape') {
              if (props.mode === 'steer' && props.onCancelSteer) props.onCancelSteer();
              else area.current?.blur();
            }
          }}
          placeholder={(narrow ? PLACEHOLDER_NARROW : PLACEHOLDER)[props.mode]}
          disabled={off}
          rows={1}
          aria-label="Instructie"
          className="flex-1 min-w-0 resize-none rounded-xl bg-white/10 border border-white/15 px-4 py-2.5 text-[15px] leading-6 text-white placeholder:text-white/50 focus:outline-none focus:border-white/40 disabled:opacity-50"
        />
        <BarButton tone="dark" label="Volgende" kbd="N" aria="Volgende (n)" onClick={props.onNext} disabled={!props.hasNext}>
          <Arrow dir="down" />
        </BarButton>
        <BarButton tone="primary" label={submitLabel} kbd="Enter" aria={`${submitLabel} (Enter)`} onClick={() => void doSubmit(value)} disabled={!value.trim() || off} busy={busy}>
          <Arrow dir="up" />
        </BarButton>
      </div>
    </div>
  );
});

/** Rij 1: de chip bij bijsturen (met kruisje) of bij een vraag; de tekst wordt afgekapt op één regel. */
function Chip(props: { icon: IconName; text: string; children?: ReactNode }) {
  return (
    <div className="flex items-center gap-2 mb-2 text-[13px] min-w-0">
      <span className={`inline-flex items-center gap-1.5 rounded-full bg-white/15 pl-3 h-7 max-w-full min-w-0 ${props.children ? 'pr-0.5' : 'pr-3'}`}>
        <Icon name={props.icon} size={12} className="shrink-0 text-amber" />
        <span className="truncate">{props.text}</span>
        {props.children}
      </span>
    </div>
  );
}

/** Rij 2: de snelknoppen in één rij die zijwaarts schuift (tot de randen van de balk), zonder schuifbalk. Het woord Snel en de toetsnummers alleen op een breed scherm met een muis. */
function QuickRow(props: { quick: QuickInstruction[]; disabled: boolean; onPick: (q: QuickInstruction) => void }) {
  if (!props.quick.length) return null;
  return (
    <div role="group" aria-label="Snelle instructies" className="flex flex-nowrap items-center gap-2 overflow-x-auto scroll-hidden -mx-4 px-4 mb-2">
      <span className="hidden md:inline shrink-0 text-[13px] text-white/60 mr-1">Snel</span>
      {props.quick.map((q) => (
        <button key={q.label} type="button" disabled={props.disabled} onClick={() => props.onPick(q)} className="shrink-0 inline-flex items-center gap-1.5 h-9 pointer-coarse:h-11 px-3 rounded-full bg-white/10 hover:bg-white/20 text-[14px] whitespace-nowrap disabled:opacity-40">
          {q.label}
          {q.key ? <span className="hidden pointer-fine:inline text-[11px] text-white/60">{q.key}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Volgende en versturen: 44 px hoog; met alleen het icoon precies 44 px breed (px-3 + icoon van 20 px). Tekst en sneltoets alleen in een container van 40 rem of breder en niet op aanraakschermen. */
function BarButton(props: { tone: 'dark' | 'primary'; label: string; kbd: string; aria: string; onClick: () => void; disabled: boolean; busy?: boolean | undefined; children: ReactNode }) {
  const tone = props.tone === 'primary' ? 'bg-accent hover:bg-accent-deep' : 'bg-bar-2 hover:bg-[#144088]';
  const wide = 'hidden @[40rem]:inline pointer-coarse:hidden';
  return (
    <button type="button" aria-label={props.aria} title={props.aria} onClick={props.onClick} disabled={props.disabled || props.busy} className={`shrink-0 inline-flex items-center justify-center gap-2 h-11 px-3 rounded-xl text-[15px] font-medium text-white whitespace-nowrap transition-colors disabled:opacity-50 ${tone}`}>
      {props.busy ? <Icon name="spinner" size={20} className="animate-spin" /> : props.children}
      <span className={wide}>{props.label}</span>
      <kbd className={`${wide} rounded px-1.5 py-0.5 text-[11px] font-sans font-medium bg-white/20`}>{props.kbd}</kbd>
    </button>
  );
}

/** Pijl omhoog (versturen) of omlaag (Volgende), in de lijnstijl van de iconen. */
function Arrow(props: { dir: 'up' | 'down' }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={props.dir === 'up' ? 'M12 19V5 M5 12l7-7 7 7' : 'M12 5v14 M5 12l7 7 7-7'} />
    </svg>
  );
}
